<template>
  <label :class="[$options.name, classList]" :data-e-ga-id="uniqueId">
    <input
      :checked="checked"
      :class="`${$options.name}__input`"
      :disabled="disabled"
      :value="value"
      type="radio"
      @change="handleChange"
      @click="handleClick"
    />
    <div :class="`${$options.name}--main-content`">
      <span v-if="$slots.icon" :class="`${$options.name}__icon`">
        <!--  @slot Use this slot for icon -->
        <slot name="icon" />
      </span>
      <div :class="`${$options.name}--content-wrapper`">
        <div :class="`${$options.name}--text-container`">
          <span v-if="label" class="tw:body-1 tw:overflow-hidden tw:text-ellipsis tw:w-full">
            {{ label }}
          </span>
          <span v-if="helper" class="tw:body-2 tw:text-default-700 tw:overflow-hidden tw:text-ellipsis tw:w-full">
            {{ helper }}
          </span>
          <!--  @slot Use this slot for custom content -->
          <slot name="custom" />
        </div>
      </div>
      <span v-if="$slots.badge" :class="`${$options.name}__badge`">
        <!--  @slot Use this slot for badge -->
        <slot name="badge" />
      </span>
      <span v-if="$slots.buttonIcon" :class="[`${$options.name}__buttonIcon`]">
        <!--  @slot Use this slot for button icon -->
        <slot name="buttonIcon" />
      </span>
      <span v-if="$slots.default" :class="[`${$options.name}__default-slot`]">
        <slot />
      </span>
    </div>
    <!--  @slot Use this slot for custom content with full container width -->
    <div v-if="$slots.customFullWidth" :class="`${$options.name}--custom-full-width-slot`">
      <slot name="customFullWidth" />
    </div>
  </label>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";
import { gtagUniversalSend } from "vue-app/plugins/edroneGtag";

export default defineComponent({
  name: "EdroneRadioButton",
  props: {
    /**
     * Value of radio button
     */
    value: {
      type: null as unknown as PropType<string | number | boolean | null>,
      required: false,
      default: null,
    },
    /**
     * Label of radio button
     */
    label: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * Description of radio button
     */
    helper: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * Checked state of radio button
     */
    checked: {
      type: Boolean,
      default: false,
    },
    /**
     * Disabled state of radio button
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Orientation of radio button
     */
    orientation: {
      type: String as PropType<"vertical" | "horizontal">,
      default: "vertical",
    },
    /**
     * Whether the input radio is controlled by the parent component
     * controlled: false: The component is "uncontrolled," managing its internal state.
     * controlled: true: The component is "controlled," and its value is managed by the parent through props.
     */
    controlled: {
      type: Boolean,
      default: false,
    },
    uniqueId: {
      type: String,
      required: true,
    },
  },
  emits: [
    /**
     * Emitted when radio button is changed and <b>controlled</b> is not set or `false`.
     *
     * Returns value of radio button.
     */
    "change",
    /**
     * Emitted when radio button is clicked and <b>controlled</b> is `true`.
     *
     * Does not change checked value.
     *
     * Does not return any value.
     */
    "click",
  ],
  setup(props, context) {
    const internalChecked = computed(() => {
      return props.checked;
    });

    function handleChange(event: Event) {
      if (event.target instanceof HTMLInputElement) {
        if (props.controlled) {
          event.target.checked = internalChecked.value;
        } else {
          context.emit("change", props.value);
          gtagUniversalSend({ component_id: props.uniqueId });
        }
      }
    }

    function handleClick() {
      if (props.controlled) {
        context.emit("click");
        gtagUniversalSend({ component_id: props.uniqueId });
      }
    }

    const classList = computed(() => {
      return {
        "EdroneRadioButton--disabled": props.disabled,
        "EdroneRadioButton--buttonIcon-visible": !!context.slots.buttonIcon,
        "EdroneRadioButton--horizontal": props.orientation === "horizontal",
      };
    });

    return {
      classList,
      handleChange,
      handleClick,
    };
  },
});
</script>

<style scoped>
@import "vue-app/assets/scss/main.css";

.EdroneRadioButton {
  padding: 9px;
  border-radius: 4px;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  position: relative;
  transition: border 150ms ease-out, background 150ms ease-out;
}

.EdroneRadioButton {
  @apply tw:gap-3 tw:bg-white tw:border tw:border-default-200 tw:rounded;
}

.EdroneRadioButton input[type="radio"] {
  display: none;
}

.EdroneRadioButton:hover {
  @apply tw:border tw:border-blue-500;
}

.EdroneRadioButton:hover:has(.EdroneRadioButton__input:checked) {
  @apply tw:border tw:border-blue-600;
}

.EdroneRadioButton--disabled {
  cursor: default;
  pointer-events: none;
  border: 1px solid transparent;
  @apply tw:bg-default-000;
}

.EdroneRadioButton:has(.EdroneRadioButton__input:checked) {
  @apply tw:bg-blue-000 tw:border tw:border-blue-500;
}

.EdroneRadioButton--disabled:has(.EdroneRadioButton__input:checked) {
  @apply tw:bg-default-000 tw:border tw:border-default-100;
}

.EdroneRadioButton__icon {
  font-size: 24px;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  cursor: pointer;
  align-items: center;
}

.EdroneRadioButton--main-content {
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  align-items: center;
  @apply tw:gap-3;
}

.EdroneRadioButton--content-wrapper {
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  min-width: 0;
  @apply tw:gap-0;
}

.EdroneRadioButton--text-container {
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  min-width: 0;
  @apply tw:gap-1;
}

.EdroneRadioButton--custom-full-width-slot {
  width: 100%;
}

.EdroneRadioButton--horizontal {
  flex-flow: column nowrap !important;
  justify-content: flex-start !important;
  align-items: stretch !important;
  @apply tw:!gap-0;
}

.EdroneRadioButton--horizontal .EdroneRadioButton--main-content {
  flex-flow: row nowrap;
  align-items: flex-start;
  @apply tw:gap-4;
}

.EdroneRadioButton--horizontal .EdroneRadioButton__icon {
  font-size: 20px;
  width: 20px;
  height: 20px;
}

.EdroneRadioButton--horizontal .EdroneRadioButton--content-wrapper {
  align-items: flex-start;
}

.EdroneRadioButton--horizontal .EdroneRadioButton--text-container {
  align-items: flex-start;
  text-align: left;
}

.EdroneRadioButton--horizontal .EdroneRadioButton__badge {
  margin-left: auto;
}

.EdroneRadioButton--horizontal .EdroneRadioButton--custom-full-width-slot {
  @apply tw:mt-2;
}

.EdroneRadioButton--buttonIcon-visible:not(.EdroneRadioButton--horizontal) .EdroneRadioButton__buttonIcon {
  position: absolute;
  @apply tw:top-2.5 tw:right-2.5;
}

.EdroneRadioButton--buttonIcon-visible.EdroneRadioButton--horizontal:not(:has(.EdroneRadioButton__badge))
  .EdroneRadioButton__buttonIcon {
  margin-left: auto;
}
</style>
